import React, { useRef, useEffect, useState, useMemo } from "react";
import { EditField } from "../types";
import colors from "../../../colors/colors";
import { ColorPicker, ColorSwatch } from "../../ColorPicker";
import { ColorValue, isColorValue } from "../../../types/color";

/**
 * Normalize a color value to 8-char RGBA hex
 * Handles both 6-char (#RRGGBB) and 8-char (#RRGGBBAA) formats
 */
const normalizeToRgba = (color: string): string => {
  if (!color) return "#000000ff";
  const normalized = color.startsWith("#") ? color : `#${color}`;
  // If it's already 8-char, return as-is
  if (normalized.length === 9) {
    return normalized;
  }
  // If it's 6-char, add full opacity
  if (normalized.length === 7) {
    return `${normalized}ff`;
  }
  // For other lengths, pad to 8-char
  return normalized.padEnd(9, "f").slice(0, 9);
};

/**
 * Validate and normalize a hex string (6-char or 8-char with alpha)
 */
const normalizeHex = (input: string): string | null => {
  const cleaned = input.replace(/^#/, "");
  // Match 6-char hex (#RRGGBB)
  if (/^[0-9A-Fa-f]{6}$/i.test(cleaned)) {
    return `#${cleaned.toLowerCase()}`;
  }
  // Match 8-char hex with alpha (#RRGGBBAA)
  if (/^[0-9A-Fa-f]{8}$/i.test(cleaned)) {
    return `#${cleaned.toLowerCase()}`;
  }
  return null;
};

interface ColorFieldProps {
  value: ColorValue | string;
  onChange: (value: ColorValue) => void;
  field: EditField;
  themeColors: Record<string, string>;
  openColorPicker: string | null;
  setOpenColorPicker: (key: string | null) => void;
}

export const ColorField: React.FC<ColorFieldProps> = ({
  value,
  onChange,
  field,
  themeColors,
  openColorPicker,
  setOpenColorPicker,
}) => {
  /**
   * Get the dropdown selected value based on the ColorValue type
   * - If type is 'theme', show the theme color name
   * - If type is 'custom' or legacy string, show 'Custom'
   */
  const getSelectedDropdownValue = (): string => {
    if (isColorValue(value) && value.type === "theme") {
      return value.value;
    }
    return ""; // Custom for custom colors or legacy strings
  };

  const selectedThemeColor = getSelectedDropdownValue();

  /**
   * Resolve the current color to a hex string for display
   */
  const resolvedColor = useMemo((): string => {
    if (!value) return "#000000ff";

    // Legacy string value
    if (typeof value === "string") {
      return normalizeToRgba(value);
    }

    // ColorValue object
    if (value.type === "custom") {
      return normalizeToRgba(value.value);
    }

    // Theme color - look up the hex value
    if (value.type === "theme") {
      const hex = themeColors[value.value];
      return hex ? normalizeToRgba(hex) : "#000000ff";
    }

    return "#000000ff";
  }, [value, themeColors]);

  /**
   * Generate a random hex color
   */
  const generateRandomColor = (): string => {
    return (
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")
    );
  };

  /**
   * Handle theme color dropdown change
   */
  const handleThemeColorChange = (colorKey: string) => {
    if (colorKey === "") {
      // Switching to custom - generate a random color
      const randomHex = generateRandomColor();
      onChange({ type: "custom", value: randomHex });
    } else {
      // Selected a theme color
      onChange({ type: "theme", value: colorKey });
    }
  };

  /**
   * Handle color picker change - always sets custom color
   */
  const handleColorPickerChange = (newValue: string) => {
    onChange({ type: "custom", value: newValue });
  };

  const isPickerOpen = openColorPicker === field.key;
  const currentColor = resolvedColor;
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [openAbove, setOpenAbove] = React.useState(false);

  // Hex input state - show 6-char if alpha is `ff`, otherwise 8-char
  const getDisplayHex = (color: string): string => {
    const normalized = normalizeToRgba(color);
    // If alpha is `ff` (fully opaque), show 6-char hex
    if (
      normalized.length === 9 &&
      normalized.slice(7, 9).toLowerCase() === "ff"
    ) {
      return normalized.slice(0, 7);
    }
    // Otherwise show 8-char hex
    return normalized;
  };

  const [hexInput, setHexInput] = useState(() => getDisplayHex(currentColor));

  // Sync hex input when resolved color changes externally
  useEffect(() => {
    setHexInput(getDisplayHex(resolvedColor));
  }, [resolvedColor]);

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setHexInput(inputValue);

    // Try to normalize and apply the hex
    const normalized = normalizeHex(inputValue);
    if (normalized) {
      const rgbaValue = normalizeToRgba(normalized);
      handleColorPickerChange(rgbaValue);
    }
  };

  const handleHexInputBlur = () => {
    // Reset to current valid hex if input is invalid
    const normalized = normalizeHex(hexInput);
    if (!normalized) {
      setHexInput(getDisplayHex(currentColor));
    } else {
      // Normalize and display (6-char if alpha is `ff`, otherwise 8-char)
      const rgbaValue = normalizeToRgba(normalized);
      setHexInput(getDisplayHex(rgbaValue));
    }
  };

  // Estimated height of the color picker popover (including padding)
  const POPOVER_HEIGHT = 300;

  // Calculate position when picker opens
  useEffect(() => {
    if (isPickerOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open above if not enough space below and more space above
      setOpenAbove(spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow);
    }
  }, [isPickerOpen]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!isPickerOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpenColorPicker(null);
      }
    };

    // Use capture phase to catch event before stopPropagation on parent elements
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isPickerOpen, setOpenColorPicker]);

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        alignItems: "center",
      }}
    >
      {/* Theme Color Dropdown */}
      <select
        value={selectedThemeColor}
        onChange={(e) => handleThemeColorChange(e.target.value)}
        className="edit-panel-select"
        style={{
          flex: 1,
          padding: "12px 14px",
          border: `1px solid ${colors.border_glass}`,
          borderRadius: "10px",
          fontSize: "13px",
          color: colors.primary,
          boxSizing: "border-box",
          transition: "all 0.15s ease",
          background: colors.white,
          cursor: "pointer",
          maxWidth: "110px",
          outline: "none",
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%237e8fa8' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 10px center",
          paddingRight: "30px",
        }}
      >
        <option value="">Custom</option>
        {Object.keys(themeColors).map((colorKey) => (
          <option key={colorKey} value={colorKey}>
            {colorKey}
          </option>
        ))}
      </select>

      {/* Color Swatch with Popover */}
      <div ref={popoverRef} style={{ position: "relative" }}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpenColorPicker(isPickerOpen ? null : field.key)}
          className="edit-panel-color-swatch"
          style={{
            width: "42px",
            height: "42px",
            border: `2px solid ${colors.border_glass}`,
            borderRadius: "10px",
            cursor: "pointer",
            padding: "3px",
            transition: "all 0.15s ease",
            backgroundColor: colors.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Open color picker"
        >
          <ColorSwatch
            color={currentColor}
            style={{ width: "100%", height: "100%" }}
            borderRadius={6}
          />
        </button>

        {/* Color Picker Popover */}
        {isPickerOpen && (
          <div
            className="edit-panel-color-popover"
            style={{
              position: "absolute",
              ...(openAbove
                ? { bottom: "calc(100% + 8px)" }
                : { top: "calc(100% + 8px)" }),
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              backgroundColor: colors.white,
              borderRadius: "12px",
              padding: "12px",
              boxShadow: `0 8px 24px ${colors.shadow_medium}, 0 0 0 1px ${colors.border_glass}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ColorPicker
              value={currentColor}
              onChange={handleColorPickerChange}
            />
          </div>
        )}
      </div>

      {/* Editable Hex Value (supports 6-char or 8-char with alpha) */}
      <input
        type="text"
        value={hexInput}
        onChange={handleHexInputChange}
        onBlur={handleHexInputBlur}
        className="edit-panel-color-text"
        placeholder="#000000 or #000000ff"
        maxLength={9}
        style={{
          width: "100px",
          padding: "12px 10px",
          border: `1px solid ${colors.border_glass}`,
          borderRadius: "10px",
          fontSize: "12px",
          color: colors.primary,
          fontFamily: "'SF Mono', 'Menlo', 'Monaco', 'Courier New', monospace",
          background: colors.white,
          outline: "none",
          transition: "all 0.15s ease",
          cursor: "text",
        }}
        onFocus={(e) => e.target.select()}
      />
    </div>
  );
};
